import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createBloodTest, updateBloodTest } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { getObjFromId } from '../../../../utils/directusFunctions';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import { formatFloat } from '../../../../utils/numberUtils';
import DateSelect from '../../../formElements/DateSelect';
import SingleSelect from '../../../formElements/SingleSelect';
import StaticField from '../../../formElements/StaticField';
import Switch from '../../../formElements/Switch';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import {
    DIABETES_RESULT,
    INVALID_RESULT,
    NORMAL_RESULT,
    PRE_DIABETES_RESULT,
    validate
} from './validateBloodTest';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const EditBloodTest = ({
    participant,
    onClose,
    isDisabled,
    attendedModules,
    linkedModuleId,
    formType,
    row = {}
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant blood test has been ${formType === 'create' ? 'added' : 'updated'}`;
    const initialState = {
        id: '',
        eventId: '',
        eventDate: todayDate(),
        sourceId: '',
        linkedModuleId: '',
        testType: 'HbA1c',
        testValueHb: null,
        testValueFpg: null,
        testValueType: 'mmol/mol',
        testResult: '',
        participantId: '',
        testValue: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [modules, setModules] = useState([]);
    const [maxTestValueLength, setMaxTestValueLength] = useState(3);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const bloodTestEvents = useSelector((state) => state.entities.directusService.bloodTestEvents);
    const bloodTestSources = useSelector(
        (state) => state.entities.directusService.bloodTestSources
    );

    // USE EFFECTS
    useEffect(() => {
        if (formType === 'create' && linkedModuleId) {
            setNewEntry({ ...initialState, linkedModuleId });
        }
        if (formType === 'edit') {
            setNewEntry({ ...row });
            row.testValueFpg && setMaxTestValueLength(5);
        }
    }, [formType, row]);

    useEffect(() => {
        let modules = [...attendedModules];
        if (formType === 'edit') {
            if (
                row.linkedModuleId &&
                row.linkedModule &&
                !modules.find((el) => el.id === row.linkedModuleId)
            ) {
                modules = [...modules, { id: row.linkedModuleId, name: row.linkedModule }];
            }
        }
        setModules(modules);
    }, [attendedModules, formType, row.linkedModuleId, row?.linkedModule]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onTestValueChange = (e) => {
        const { value } = e.target;
        clearError('testValue');

        let testResult;
        let testValue;

        if (newEntry.testType === 'HbA1c') {
            testValue = value.replace(regExps.DIGITS_R, '');
            if (testValue >= 31 && testValue <= 41) testResult = NORMAL_RESULT;
            else if (testValue >= 42 && testValue <= 47) testResult = PRE_DIABETES_RESULT;
            else if (testValue >= 48 && testValue <= 119) testResult = DIABETES_RESULT;
            else testResult = INVALID_RESULT;
        }

        if (newEntry.testType === 'FPG') {
            testValue = formatFloat(value);
            if (testValue >= 2 && testValue <= 5.4) testResult = NORMAL_RESULT;
            else if (testValue >= 5.5 && testValue <= 6.9) testResult = PRE_DIABETES_RESULT;
            else if (testValue >= 7.0 && testValue <= 13.0) testResult = DIABETES_RESULT;
            else testResult = INVALID_RESULT;
        }
        setNewEntry((prev) => ({ ...prev, testValue, testResult }));
    };

    const onSwitchTestType = (e) => {
        const { checked } = e.target;
        clearError('testValue');
        setNewEntry((prev) => ({
            ...prev,
            testType: checked ? 'FPG' : 'HbA1c',
            testValueType: checked ? 'mmol/l' : 'mmol/mol',
            testValue: '',
            testResult: ''
        }));
        setMaxTestValueLength(checked ? 5 : 3);
    };

    const onSubmit = (e) => {
        e.preventDefault();

        const updatedEntry = {
            ...newEntry,
            testValueHb: newEntry.testType === 'HbA1c' ? +newEntry.testValue : null,
            testValueFpg: newEntry.testType === 'FPG' ? +newEntry.testValue : null
        };
        const newErrors = validate(updatedEntry, row, formType);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        // eslint-disable-next-line
        const { event, source, linkedModule, testValue, ...rest } = updatedEntry;
        const payload = { ...rest, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createBloodTest(payload, _MESSAGE))
            : dispatch(updateBloodTest(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form className={form.form} onSubmit={onSubmit} data-testid="edit_blood_test_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add a New Blood Test' : 'Edit Blood Test'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateSelect
                            id="eventDate"
                            label="Event Date"
                            value={newEntry.eventDate}
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.eventDate}
                            onDateChange={(date) => onUpdate('eventDate', date)}
                        />

                        <SingleSelect
                            id="sourceId"
                            label="Source"
                            placeholder="Select source..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={bloodTestSources || []}
                            selectedId={newEntry.sourceId || ''}
                            selected={getObjFromId(bloodTestSources, newEntry.sourceId)}
                            error={errors.sourceId}
                            onChange={(chosenId) => onUpdate('sourceId', chosenId)}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <SingleSelect
                            id="eventId"
                            label="Event"
                            placeholder="Select event..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={bloodTestEvents || []}
                            selectedId={newEntry.eventId || ''}
                            selected={getObjFromId(bloodTestEvents, newEntry.eventId)}
                            error={errors.eventId}
                            onChange={(chosenId) => onUpdate('eventId', chosenId)}
                        />
                    </div>
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <Switch
                            id="testType"
                            label="Test Type"
                            leftLabel="HbA1c"
                            rightLabel="FPG"
                            disabled={isDisabled}
                            checked={newEntry.testType === 'FPG' || false}
                            onChange={onSwitchTestType}
                        />

                        <TextInputField
                            id="testValue"
                            label="Test Value"
                            placeholder="Enter test value..."
                            mandatory={true}
                            disabled={isDisabled}
                            maxLength={maxTestValueLength}
                            value={newEntry.testValue || ''}
                            error={errors.testValue}
                            onChange={onTestValueChange}
                        />

                        <SingleSelect
                            id="linkedModuleId"
                            label="Linked Module"
                            placeholder="Select linked module..."
                            disabled={isDisabled}
                            menuItems={modules || []}
                            selectedId={newEntry.linkedModuleId}
                            selected={getObjFromId(modules, newEntry.linkedModuleId)}
                            onChange={(chosenId) => onUpdate('linkedModuleId', chosenId)}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <StaticField
                            id="testValueType"
                            label="Test Value Type"
                            content={newEntry.testValueType || ' - '}
                            customClass="info"
                        />
                        <StaticField
                            id="testResult"
                            label="Test Result"
                            content={newEntry.testResult || ' - '}
                            customClass="info"
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    disabled={isButtonDisabled || isDisabled}
                    customClass="noTopBorder"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditBloodTest.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    attendedModules: PropTypes.arrayOf(PropTypes.object),
    linkedModuleId: PropTypes.string,
    formType: PropTypes.string,
    row: PropTypes.object
};

export default EditBloodTest;
